import translate from "i18n/translate";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import ApiRequest from "../../actions/ApiRequest";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import Modal from "components/common/Modal";
import TikTokCard from "./TiktokCard";
import Information from "./Information";
import UserDetails from "./UserDetails";
import Toast from "components/common/Toast";
import { useIntl } from "react-intl";
import { LoadingButton } from "@mui/lab";
import SetHomePageButton from "../common/SetHomePageButton";
import useIsMobile from "shared/hooks/useIsMobile";

const Tiktok = ({ isDarktheme }) => {
  const IS_MOBILE_DISPLAY = useIsMobile();
  const intl = useIntl();
  const [coins, setCoins] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [selectedCardsIds, setSelectedCardsIds] = useState({});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isCharging, setIsCharging] = useState(false);
  const [mobileNo, setMobileNo] = useState("");

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        const response = await ApiRequest.post("get_tiktok_coins");
        if (response.data.status === "failed") {
          throw new Error(response.data.message);
        }
        setCoins(response.data);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (Object.keys(selectedCardsIds).length > 0) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [selectedCardsIds]);

  const onSelectCard = useCallback((id, quantity) => {
    if (quantity === 0) {
      setSelectedCardsIds((prev) => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setSelectedCardsIds((prev) => ({
        ...prev,
        [id]: quantity,
      }));
    }
  }, []);

  const getTiktokCards = useCallback(
    (id) => {
      return coins.find((coin) => coin.id === id);
    },
    [coins]
  );

  const getSelectedTiktokCards = useCallback(() => {
    return Object.entries(selectedCardsIds)
      .filter(([_, quantity]) => quantity > 0)
      .map(([id, quantity]) => {
        const card = getTiktokCards(id);
        return {
          ...card,
          quantity,
        };
      });
  }, [selectedCardsIds, getTiktokCards]);

  const chargeTiktok = useCallback(async () => {
    setIsCharging(true);
    try {
      const cardIds = getSelectedTiktokCards();

      const requests = cardIds.flatMap((card) => {
        return Array(card.quantity)
          .fill()
          .map(() => {
            return ApiRequest.post(
              `tiktok?id=${card.id}&mobile_no=${mobileNo}`
            );
          });
      });

      await Promise.all(requests);
      setOpenModal(false);
      setSelectedCardsIds({});
      setMobileNo("");
      Toast.fire({
        title: intl.formatMessage({
          id: "Your request is in progress",
        }),
        icon: "success",
      });
    } catch (e) {
      setOpenModal(false);
      console.log(e);
      await Toast.fire({
        title: intl.formatMessage({
          id: "tiktok_coins_addition_failed",
        }),
        icon: "error",
      });
    } finally {
      setIsCharging(false);
      setSelectedCardsIds({});
      setMobileNo("");
    }
  }, [getSelectedTiktokCards, mobileNo, intl]);

  const handleConfirm = useCallback((fullPhoneNumber) => {
    setMobileNo(fullPhoneNumber);

    const totalCoins = coins.reduce(
      (acc, curr) => acc + (selectedCardsIds[curr.id] || 0) * curr.coins,
      0
    );
    const customerPrice = coins.reduce(
      (acc, curr) =>
        acc + (selectedCardsIds[curr.id] || 0) * curr.customer_price,
      0
    );
    const sellerPrice = coins.reduce(
      (acc, curr) => acc + (selectedCardsIds[curr.id] || 0) * curr.seller_price,
      0
    );

    const formattedMessage = intl.formatMessage(
      { id: "tiktokConfirmationMessage" },
      {
        phone_number: `<span style="font-weight: bold; font-size: 1.2em; ">${fullPhoneNumber}</span>`,
        total_coins: `<span style="font-weight: bold; font-size: 1.2em; ">${totalCoins}</span>`,
        customer_price: `<span style="font-weight: bold; font-size: 1.2em; ">${customerPrice}</span>`,
        seller_price: `<span style="font-weight: bold; font-size: 1.2em; ">${sellerPrice}</span>`,
      }
    );

    setConfirmMessage(formattedMessage);
    setOpenModal(true);
  }, [coins, selectedCardsIds, intl]);

  return (
    <div>
      <div className="card card-home mb-4">
        <div className={!isDarktheme ? "card p-4" : "dark-card p-4"}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h1 className="header-text">{translate("tiktok_welcome")}</h1>
            <SetHomePageButton 
              currentPath="/tiktok"
              pageName="TikTok"
              variant="icon"
            />
          </div>
        </div>
      </div>
      {isLoading ? (
        <Box
          sx={{ display: "flex", position: "fixed", left: "50%", top: "40%" }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <UserDetails
            onConfirm={handleConfirm}
            buttonDisabled={buttonDisabled}
          />
          <Alert severity="warning">{translate("phoneWarning")}</Alert>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mt: 2,
            }}
          >
            {IS_MOBILE_DISPLAY && (
              <Information selectedCardsIds={selectedCardsIds} coins={coins} />
            )}
            {coins.map((coin) => (
              <TikTokCard
                coin={coin}
                onClick={onSelectCard}
                isSelected={selectedCardsIds[coin.id] ? true : false}
                cardQuantity={selectedCardsIds[coin.id] || 1}
                key={coin.id}
              />
            ))}
          </Box>
          {!IS_MOBILE_DISPLAY && (
            <Information selectedCardsIds={selectedCardsIds} coins={coins} />
          )}
          {openModal && (
            <Modal
              visible={openModal}
              onClose={() => setOpenModal(false)}
              width={"45vw"}
              title={intl.formatMessage({ id: "Confirm" })}
            >
              <Typography
                variant="h6"
                dangerouslySetInnerHTML={{ __html: confirmMessage }}
              />
              <div
                className="d-flex justify-content-end align-items-center mt-4"
                style={{ width: "100%" }}
              >
                <button
                  className="btn btn-danger mx-3"
                  onClick={() => setOpenModal(false)}
                >
                  {translate("Cancel")}
                </button>
                <LoadingButton
                  onClick={chargeTiktok}
                  loading={isCharging}
                  variant="contained"
                >
                  {translate("Confirm")}
                </LoadingButton>
              </div>
            </Modal>
          )}
        </Box>
      )}
    </div>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => {
  return {
    isDarktheme,
  };
};

export default connect(mapStateToProps)(Tiktok);
